/**
 * The two controls the designer steers the interview with.
 *
 * Shared by the create screen and the project's settings panel, because the
 * two would otherwise drift — and because the copy on them is the explanation
 * of what each setting does, which should not exist twice.
 *
 * Both settings steer; neither constrains. There is no system-prompt box,
 * because what the interviewer sounds like is copy under review rather than a
 * per-project field. There is no list of questions it has to get through
 * either — that was tried, and it turned an interviewer that follows what it
 * is hearing into one working through an agenda.
 */

import { StepSlider, type Stop } from './StepSlider';
import { ds } from '../ds';
import type { InterviewSettings, InterviewTone } from '../api';

/** Mirrors TONE_LINE in worker/lib/ai/prompts.ts — these descriptions are the
 *  promise the prompt has to keep. */
export const TONE_STOPS: ReadonlyArray<Stop<InterviewTone>> = [
  { value: 'formal', label: 'Formal', description: 'Full sentences, no contractions.' },
  { value: 'neutral', label: 'Neutral', description: 'Plain and even.' },
  { value: 'warm', label: 'Warm', description: 'Friendly, with the odd short aside.' }
];

export const MAX_GUIDE_CHARS = 600;

export const emptySettings = (): InterviewSettings => ({ tone: 'neutral', questionGuide: null });

interface Props {
  value: InterviewSettings;
  onChange: (next: InterviewSettings) => void;
  disabled?: boolean;
}

export function InterviewSettingsFields({ value, onChange, disabled = false }: Props) {
  const { Input } = ds();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
      <StepSlider
        label="How it sounds"
        stops={TONE_STOPS}
        value={value.tone}
        onChange={(tone) => onChange({ ...value, tone })}
        disabled={disabled}
      />

      <Input
        label="What should it cover?"
        multiline
        rows={3}
        value={value.questionGuide ?? ''}
        disabled={disabled}
        onChange={(e) =>
          onChange({ ...value, questionGuide: e.target.value.slice(0, MAX_GUIDE_CHARS) || null })
        }
        placeholder="Rejection reasons by claim type, and who signs off changes to the form."
        hint="Subjects to steer towards. The questions are still written to suit the answers."
      />
    </div>
  );
}

/** Trim what an in-progress edit carries before sending it. */
export const forWire = (s: InterviewSettings): InterviewSettings => ({
  tone: s.tone,
  questionGuide: s.questionGuide?.trim() ? s.questionGuide.trim() : null
});
